import { useEffect, useState } from "react";
import api, { trackingBase } from "../lib/api";
import { PageHeader, Card } from "../components/shared";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Wand2, Copy, Globe, Gift, Link2 } from "lucide-react";

const CopyBlock = ({ label, code, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <button
        data-testid={`copy-${testId}`}
        onClick={() => { navigator.clipboard.writeText(code); toast.success(`${label} copied`); }}
        className="text-slate-400 hover:text-indigo-600 transition-colors"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
    <pre className="bg-slate-900 rounded-lg p-3 text-[11px] text-emerald-400 overflow-x-auto scrollbar-thin whitespace-pre-wrap break-all"><code data-testid={testId}>{code}</code></pre>
  </div>
);

export default function LinkGenerator() {
  const [sources, setSources] = useState([]);
  const [offers, setOffers] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [offerId, setOfferId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/traffic-sources"), api.get("/offers"), api.get("/networks")]).then(([s, o, n]) => {
      setSources(s.data); setOffers(o.data); setNetworks(n.data);
    });
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/placements/generate", { traffic_source_id: sourceId, offer_id: offerId });
      setResult(res.data);
      toast.success(res.data.created ? "New placement campaign created" : "Existing placement found — link ready");
    } finally {
      setBusy(false);
    }
  };

  const cmp = result?.campaign;
  const offer = offers.find((o) => o.id === offerId);
  const network = networks.find((n) => n.id === offer?.network_id);
  const trackUrl = cmp ? `${trackingBase(network)}/api/t/${cmp.id}` : "";

  return (
    <div data-testid="link-generator-page">
      <PageHeader title="Link Generator" subtitle="Pick a website + offer → get a banner / CTA tracking link ready to paste on that website" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 fade-up">
        <Card className="p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-indigo-500" /> Build Placement
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs flex items-center gap-1.5"><Globe className="h-3 w-3" /> Website / Traffic Source</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger data-testid="generator-source-select" className="mt-1"><SelectValue placeholder="Select website" /></SelectTrigger>
                <SelectContent>{sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5"><Gift className="h-3 w-3" /> Offer</Label>
              <Select value={offerId} onValueChange={setOfferId}>
                <SelectTrigger data-testid="generator-offer-select" className="mt-1"><SelectValue placeholder="Select offer" /></SelectTrigger>
                <SelectContent>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {offer && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                <span className="font-semibold">{offer.name}</span> · {network?.name} · {offer.payout_type} ${offer.payout} · Geo: {(offer.geo || []).join(", ") || "All"}
              </div>
            )}
            <Button data-testid="generate-link-button" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!sourceId || !offerId || busy} onClick={generate}>
              <Link2 className="h-4 w-4 mr-2" /> {busy ? "Generating…" : "Generate Tracking Link"}
            </Button>
            <p className="text-[11px] text-slate-400">A placement campaign is created automatically for this website × offer pair (or reused if it exists), so stats appear per placement in Campaigns and Reports.</p>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Paste-Ready Code</h3>
          {!result ? (
            <div className="text-xs text-slate-400">Select a website and offer, then generate. You'll get the direct URL plus banner & CTA snippets.</div>
          ) : (
            <div className="space-y-4" data-testid="generator-result">
              <div className="text-xs text-slate-500">Placement: <span className="font-semibold text-slate-800">{cmp.name}</span> <code className="text-[10px] text-indigo-600 ml-1">{cmp.id}</code></div>
              <CopyBlock label="Direct Tracking URL" testId="direct-url" code={trackUrl} />
              <CopyBlock
                label="CTA Button / Text Link (recommended — carries email identity via snippet)"
                testId="cta-code"
                code={`<a data-omni-campaign="${cmp.id}" href="${trackUrl}">Check Your Rate →</a>`}
              />
              <CopyBlock
                label="Banner Ad"
                testId="banner-code"
                code={`<a data-omni-campaign="${cmp.id}" href="${trackUrl}">\n  <img src="YOUR_BANNER_IMAGE_URL" alt="${offer?.name || "offer"}" style="max-width:100%">\n</a>`}
              />
              <p className="text-[11px] text-slate-400">If the website tracking snippet (Traffic Sources page) is installed, the data-omni-campaign attribute auto-attaches the visitor's email identity, sub IDs and UTMs to the click. Without the snippet, the plain href still tracks the click anonymously.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
